import React from "react"
import {
  Box,
  ButtonBase,
  Stack,
  Tooltip,
  Typography,
  type ButtonBaseProps,
} from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { Badge } from "../atoms/Badge"
import { ViraIcon, type ViraIconName } from "../atoms/ViraIcon"
import { typeScale } from "../theme/tokens"
import { useSidebarMenu } from "./SidebarMenu"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type SidebarMenuItemVariant = "section" | "item"

export type SidebarMenuItemProps = Omit<ButtonBaseProps, "children" | "title"> & {
  label: string
  active?: boolean
  disabled?: boolean
  variant?: SidebarMenuItemVariant
  /** Group headers (Treat / Wrap up) use compact uppercase styling */
  group?: boolean
  badge?: string | number
  icon?: ViraIconName
  showChevron?: boolean
  depth?: number
  /**
   * Icon-only rail mode. When omitted, inherits from parent `SidebarMenu`
   * via context.
   */
  collapsed?: boolean
}

function labelInitials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase()
}

export function SidebarMenuItem({
  label,
  active = false,
  disabled = false,
  variant = "item",
  group = false,
  badge,
  icon,
  showChevron = variant === "section",
  depth = variant === "item" ? 1 : 0,
  collapsed: collapsedProp,
  onClick,
  sx,
  ...rest
}: SidebarMenuItemProps) {
  const { collapsed: contextCollapsed } = useSidebarMenu()
  const collapsed = collapsedProp ?? contextCollapsed
  const isSection = variant === "section"
  const isGroupHeader = isSection && group

  // Collapsed rail hides group headers (TODAY / TREAT / WRAP UP labels)
  if (collapsed && isGroupHeader) {
    return null
  }

  const initials = labelInitials(label)
  const button = (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      aria-label={collapsed ? label : undefined}
      sx={composeSx(
        (theme) => ({
          width: "100%",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 3,
          pl: collapsed ? 1 : depth > 0 ? 5 : 3,
          py: collapsed ? 1.25 : isSection ? 1.25 : 1,
          minHeight: 40,
          backgroundColor: active ? "vira.navActiveBg" : "transparent",
          borderLeft: active
            ? `3px solid ${resolveViraPalette(theme).accent}`
            : "3px solid transparent",
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          "&:hover": {
            backgroundColor: disabled
              ? "transparent"
              : active
                ? "vira.navActiveBg"
                : "vira.pageBg",
          },
        }),
        sx,
      )}
      {...rest}
    >
      {collapsed ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%" }}
        >
          {icon ? (
            <ViraIcon name={icon} tone={active ? "ink" : "muted"} />
          ) : (
            <Typography
              aria-hidden
              sx={{
                fontSize: typeScale.meta,
                fontWeight: 700,
                letterSpacing: "0.4px",
                color: active ? "vira.ink" : "vira.muted",
                lineHeight: 1,
              }}
            >
              {initials}
            </Typography>
          )}
        </Stack>
      ) : (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            {showChevron ? (
              <Typography
                sx={{ fontSize: typeScale.meta, color: "vira.subtle", lineHeight: 1 }}
              >
                ▾
              </Typography>
            ) : null}
            {icon ? <ViraIcon name={icon} tone={active ? "ink" : "muted"} /> : null}
            <Typography
              sx={{
                fontSize: isGroupHeader ? typeScale.meta : typeScale.body,
                fontWeight: active || isSection ? 600 : 400,
                letterSpacing: isGroupHeader ? "1.5px" : 0,
                textTransform: isGroupHeader ? "uppercase" : "none",
                color: active || isSection ? "vira.ink" : "vira.muted",
                textAlign: "left",
              }}
            >
              {label}
            </Typography>
          </Stack>
          {badge != null ? <Badge label={badge} /> : null}
        </>
      )}
    </ButtonBase>
  )

  if (!collapsed) return button

  return (
    <Tooltip title={label} placement="right" enterDelay={300} describeChild>
      <Box component="span" sx={{ display: "block", width: "100%" }}>
        {button}
      </Box>
    </Tooltip>
  )
}
